import DoctorSubscriptionService from "services/DoctorSubscriptionService"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

export const getDoctorsWithSubscriptions = createAsyncThunk(
  "doctorSubscription/getDoctorsWithSubscriptions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { limit = 10, page = 1 } = params
      const response = await DoctorSubscriptionService.getAllDoctorsWithSubscriptions(page, limit)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const getDoctorSubscription = createAsyncThunk(
  "doctorSubscription/getDoctorSubscription",
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await DoctorSubscriptionService.getDoctorSubscription(doctorId)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const assignPlanToDoctor = createAsyncThunk(
  "doctorSubscription/assignPlanToDoctor",
  async ({ doctorId, planData }, { rejectWithValue }) => {
    try {
      const response = await DoctorSubscriptionService.assignPlanToDoctor(doctorId, planData)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const renewDoctorSubscription = createAsyncThunk(
  "doctorSubscription/renewDoctorSubscription",
  async ({ doctorId, planType }, { rejectWithValue }) => {
    try {
      const response = await DoctorSubscriptionService.renewDoctorSubscription(doctorId, planType)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

export const cancelDoctorSubscription = createAsyncThunk(
  "doctorSubscription/cancelDoctorSubscription",
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await DoctorSubscriptionService.cancelDoctorSubscription(doctorId)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const initialState = {
  currentDoctor: null,
  currentPage: 1,
  doctors: [],
  error: null,
  loading: false,
  pages: 0,
  selectedDoctor: null,
  subscriptionModalOpen: false,
  totalItems: 0,
}

const doctorSubscriptionSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Get doctors with subscriptions
      .addCase(getDoctorsWithSubscriptions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getDoctorsWithSubscriptions.fulfilled, (state, action) => {
        state.loading = false
        state.doctors = action.payload.data || []
        state.totalItems = action.payload.totalItems || 0
        state.pages = action.payload.pages || 0
      })
      .addCase(getDoctorsWithSubscriptions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Get doctor subscription
      .addCase(getDoctorSubscription.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getDoctorSubscription.fulfilled, (state, action) => {
        state.loading = false
        state.currentDoctor = action.payload
      })
      .addCase(getDoctorSubscription.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Assign plan to doctor
      .addCase(assignPlanToDoctor.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(assignPlanToDoctor.fulfilled, (state, action) => {
        state.loading = false
        state.subscriptionModalOpen = false
        // Refresh the doctors list
        // You might want to dispatch getDoctorsWithSubscriptions here
      })
      .addCase(assignPlanToDoctor.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Renew subscription
      .addCase(renewDoctorSubscription.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(renewDoctorSubscription.fulfilled, (state, action) => {
        state.loading = false
        state.subscriptionModalOpen = false
      })
      .addCase(renewDoctorSubscription.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Cancel subscription
      .addCase(cancelDoctorSubscription.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(cancelDoctorSubscription.fulfilled, (state, action) => {
        state.loading = false
        state.subscriptionModalOpen = false
      })
      .addCase(cancelDoctorSubscription.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
  initialState,
  name: "doctorSubscription",
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setSelectedDoctor: (state, action) => {
      state.selectedDoctor = action.payload
    },
    setSubscriptionModal: (state, action) => {
      state.subscriptionModalOpen = action.payload
    },
  },
})

export const {
  clearError,
  setCurrentPage,
  setSelectedDoctor,
  setSubscriptionModal,
} = doctorSubscriptionSlice.actions

export default doctorSubscriptionSlice.reducer